import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { paymentService } from "@/services/paymentService";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const data = await paymentService.status(sessionId);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired" || attempts.current >= 6) { setStatus("error"); return; }
      } catch {
        if (attempts.current >= 6) { setStatus("error"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refresh]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
      <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg mb-10">
        <GraduationCap className="h-6 w-6 text-primary" /> Learn Me
      </Link>
      {status === "checking" && (<>
        <Loader2 className="h-12 w-12 text-primary animate-spin mb-6" />
        <h1 className="font-display text-2xl font-bold">Confirming your payment...</h1>
        <p className="text-muted-foreground mt-2">Hang tight, this only takes a moment.</p>
      </>)}
      {status === "paid" && (<>
        <CheckCircle2 className="h-14 w-14 text-primary mb-6" data-testid="payment-success-icon" />
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Payment successful! 🎉</h1>
        <p className="text-muted-foreground mt-2 max-w-sm">Your account has been updated. Time to keep learning.</p>
        <Button onClick={() => navigate("/learn")} className="mt-8 rounded-none h-11 px-8 bg-primary hover:opacity-90">Create a course</Button>
      </>)}
      {status === "error" && (<>
        <XCircle className="h-14 w-14 text-destructive mb-6" />
        <h1 className="font-display text-2xl font-bold">We couldn't confirm payment</h1>
        <p className="text-muted-foreground mt-2 max-w-sm">If you were charged, your plan will update shortly. Contact support if needed.</p>
        <Button onClick={() => navigate("/pricing")} variant="outline" className="mt-8 rounded-none h-11 px-8">Back to pricing</Button>
      </>)}
    </div>
  );
}
